<template>
  <div v-if="multiSitePayload.length > 0 && renderComponent">
    <div class="columns">
      <div class="column">
        <b-checkbox
          :class="$style.inputCheckbox"
          v-model="validateDataEntry"
          :disabled="!editable"
          >Validate when performing data collection beyond max
          value.</b-checkbox
        >
      </div>
    </div>
    <div class="columns">
      <div class="column" :class="$style.headerTitleColumn">
        <label
          :class="[$style.tableName, $style.criteriaBasedLabel]"
          class="label"
          >CRITERIA BASED SAMPLING (OPTIONAL)</label
        >
      </div>
    </div>
    <table :class="$style.criteriaBasedTable">
      <thead>
        <tr>
          <th width="2%">No.</th>
          <th field="criteria" width="40%">Data Collection Period Criteria</th>

          <th width="100%" field="Min">
            <b-tooltip
              v-if="tooltipMinErrorMessage.length > 0"
              :label="tooltipMinErrorMessage"
              :class="$style.infoTooltip"
              multilined
              position="is-bottom"
            >
              <b-icon
                icon="information-outline"
                size="is-small"
                custom-size="mdi-15px"
                :custom-class="$style.infoIcon"
              >
              </b-icon>
            </b-tooltip>
            Min.
          </th>
          <th width="100px" field="Max" label="Max.">
            <b-tooltip
              v-if="tooltipMaxErrorMessage.length > 0"
              :label="tooltipMaxErrorMessage"
              :class="$style.infoTooltip"
              multilined
              position="is-bottom"
            >
              <b-icon
                icon="information-outline"
                size="is-small"
                custom-size="mdi-15px"
                :custom-class="$style.infoIcon"
              >
              </b-icon>
            </b-tooltip>
            Max.
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, itemIndex) in auditCriteriaList" :key="itemIndex">
          <td width="2%">{{ itemIndex + 1 }}.</td>
          <td width="40%">
            {{ item.criteriaTitle }}
          </td>

          <td width="100%">
            <bField horizontal class="sampleSizeInputContainer">
              <b-input
                :disabled="!editable"
                v-model="item.minSampleSize"
                type="number"
                onkeydown="javascript: return event.keyCode === 8 || event.keyCode === 46 ? true : !isNaN(Number(event.key))"
                :class="
                  !isParentSamplingValid(item.criteriaId, 'min')
                    ? [$style.invalidInput, $style.numberInput]
                    : $style.numberInput
                "
                min="0"
                @change.native="
                  validateRangeAndUpdateChildSamplingSize(
                    itemIndex,
                    'min',
                    item.minSampleSize,
                    item.maxSampleSize,
                  )
                "
                oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);"
                maxlength="7"
              >
              </b-input>
            </bField>

            <template v-for="(site, siteIndex) in siteArray">
              <bField
                horizontal
                class="multiSiteConsecutiveMinMaxInputContainer"
                :label="site.name"
                :key="siteIndex"
              >
                <b-input
                  :disabled="isSiteInputDisable('min', item.criteriaId)"
                  v-model="item.sites[siteIndex].minSampleSize"
                  type="number"
                  onkeydown="javascript: return event.keyCode === 8 || event.keyCode === 46 ? true : !isNaN(Number(event.key))"
                  :class="
                    !isSiteSamplingValid(item.criteriaId, site.siteId)
                      ? [$style.invalidInput, $style.numberInput]
                      : $style.numberInput
                  "
                  @input="validateRangeOnChange()"
                  min="0"
                  oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);"
                  maxlength="7"
                ></b-input>
              </bField>
            </template>
          </td>
          <td width="100px">
            <bField class="sampleSizeInputContainer">
              <b-input
                :disabled="!editable"
                v-model="item.maxSampleSize"
                type="number"
                onkeydown="javascript: return event.keyCode === 8 || event.keyCode === 46 ? true : !isNaN(Number(event.key))"
                :class="
                  !isParentSamplingValid(item.criteriaId, 'max')
                    ? [$style.invalidInput, $style.numberInput]
                    : $style.numberInput
                "
                min="0"
                @change.native="
                  validateRangeAndUpdateChildSamplingSize(
                    itemIndex,
                    'max',
                    item.minSampleSize,
                    item.maxSampleSize,
                  )
                "
                oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);"
                maxlength="7"
              ></b-input>
            </bField>
            <template v-for="(site, siteIndex) in siteArray">
              <bField class="sampleSizeInputContainer" :key="siteIndex">
                <b-input
                  :disabled="isSiteInputDisable('max', item.criteriaId)"
                  v-model="item.sites[siteIndex].maxSampleSize"
                  type="number"
                  onkeydown="javascript: return event.keyCode === 8 || event.keyCode === 46 ? true : !isNaN(Number(event.key))"
                  :class="$style.numberInput"
                  min="0"
                  @input="validateRangeOnChange()"
                  oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);"
                  maxlength="7"
                ></b-input>
              </bField>
            </template>
          </td>
        </tr>
      </tbody>
    </table>
    <div :class="$style.saveChangesContainer" v-if="editable">
      <b-button
        :disabled="isButtonDisabled"
        @click="handleMultiSiteChanges"
        :class="$style.saveChangesBtn"
        type="is-primary"
        >Save</b-button
      >
    </div>
  </div>
</template>
<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import { Action, State } from 'vuex-class';
import HeroBar from '@/components/base/HeroBar.vue';
import { isDifferent, isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import {
  AuditSamplingSite,
  MultiSitePayload,
  MultiSiteSamplingRequestPayload,
  AuditSite,
  AuditSamplingResponse,
  AuditSamplingDataConfig,
  RangeCriteriaTypeSamplingValidation,
  RangeSamplingValidation,
  AuditCriteriaMapStatus,
} from '@/store/modules/audits/types/audits.types';
import {
  map as _map,
  round as _round,
  cloneDeep as _cloneDeep,
  orderBy as _orderBy,
  findIndex as _findIndex,
  find as _find,
} from 'lodash';
import { ToastProgrammatic as Toast } from 'buefy';
import SamplingCalculator from '@/views/Audit/components/SamplingCalculator.vue';
import {
  AuditSamplingType,
  ConsecutiveSamplingMode,
} from '@/jbi-shared/types/project.types';

@Component({
  components: {
    SamplingCalculator,
    DefaultLayout,
    HeroBar,
  },
})
export default class ConsecutiveMinAndMaxMultiSiteSampling extends Vue {
  @Prop()
  public auditId!: number;
  @Prop()
  public auditSiteList!: AuditSite[];
  @Prop()
  public auditConfig!: AuditSamplingResponse;
  @Prop()
  public auditSamplingType!: AuditSamplingType;
  @Prop()
  public consecutiveSamplingMode!: ConsecutiveSamplingMode;
  @Prop()
  public validateCurrentDataEntryValue!: boolean;
  @Prop({ default: true })
  public editable!: boolean;
  public auditStartDate: any = null;
  public multiSitePayload: MultiSitePayload[] = [];
  public siteArray: AuditSamplingSite[] = [];
  public renderComponent: boolean = true;
  public validateDataEntry: boolean = false;
  public isButtonDisabled: boolean = false;
  public isButtonClick: boolean = false;
  public autoDistribute: boolean = true;
  public invalidSamplingSites: RangeCriteriaTypeSamplingValidation[] = [];
  public tooltipMinErrorMessage: string = '';
  public tooltipMaxErrorMessage: string = '';

  @Action('audits/updateMultiSiteSampling')
  public updateMultiSiteSampling!: (
    payload: MultiSiteSamplingRequestPayload,
  ) => void;

  @State((state) => state.audits.updatedMultiSiteSampling)
  public updatedMultiSiteSampling!: any;

  @Watch('updatedMultiSiteSampling')
  @isTruthy
  public watchUpdatedMultiSiteSampling() {
    this.showNotificationAndRedirectToProject();
  }

  public mounted() {
    this.validateDataEntry = this.validateCurrentDataEntryValue;
    this.populateMultiSiteData();
  }

  public populateMultiSiteData() {
    this.auditSiteList.forEach((auditSite: AuditSite) => {
      const siteObject: AuditSamplingSite = {
        siteId: auditSite.site.id,
        name: auditSite.site.name,
        minSampleSize: null,
        maxSampleSize: null,
        auditSiteId: auditSite.id,
      };
      this.siteArray.push(siteObject);
    });

    this.auditConfig.dataConfigs.forEach(
      (dataConfig: AuditSamplingDataConfig) => {
        const currentCriteriaPayload: MultiSitePayload = {
          auditId: this.auditId,
          criteriaId: 0, // todo: change this logic later
          auditCriteriaId: 0,
          sites: [],
          minSampleSize: null,
          maxSampleSize: null,
        };
        Object.values(dataConfig).forEach(
          (singleConfig: AuditSamplingDataConfig) => {
            if (
              singleConfig.auditCriteriaMap.status ===
              AuditCriteriaMapStatus.ACTIVE
            ) {
              currentCriteriaPayload.criteriaId =
                singleConfig.auditCriteriaMap.criteria.id;
              currentCriteriaPayload.auditId = this.auditId;
              currentCriteriaPayload.auditCriteriaId =
                singleConfig.auditCriteriaMap.id;
              currentCriteriaPayload.criteriaTitle =
                singleConfig.auditCriteriaMap.criteria.title;
              currentCriteriaPayload.order =
                singleConfig.auditCriteriaMap.order;
              if (singleConfig.auditSiteMap === null) {
                currentCriteriaPayload.minSampleSize =
                  singleConfig.minSampleSize !== null
                    ? _round(singleConfig.minSampleSize)
                    : null;
                currentCriteriaPayload.maxSampleSize =
                  singleConfig.maxSampleSize !== null
                    ? _round(singleConfig.maxSampleSize)
                    : null;
                currentCriteriaPayload.dataConfig = singleConfig;
              } else {
                const auditSamplingConfig = _find(
                  this.siteArray,
                  (value: AuditSamplingSite) => {
                    return value.auditSiteId === singleConfig.auditSiteMap.id;
                  },
                );
                if (auditSamplingConfig) {
                  currentCriteriaPayload.sites.push({
                    ...auditSamplingConfig,
                    minSampleSize:
                      singleConfig.minSampleSize !== null
                        ? _round(singleConfig.minSampleSize)
                        : null,
                    maxSampleSize:
                      singleConfig.maxSampleSize !== null
                        ? _round(singleConfig.maxSampleSize)
                        : null,
                    dataConfig: singleConfig,
                  });
                }
              }
            }
          },
        );
        currentCriteriaPayload.sites = _orderBy(
          currentCriteriaPayload.sites,
          ['name'],
          ['asc'],
        );
        this.multiSitePayload.push(currentCriteriaPayload);
      },
    );
    this.multiSitePayload = this.multiSitePayload.map(
      (criteria: MultiSitePayload) => {
        const sitesIdsToCheckAgainst: number[] = this.siteArray.map(
          (site: AuditSamplingSite) => site.siteId,
        );
        return {
          ...criteria,
          sites: criteria.sites.sort(
            (optionA: AuditSamplingSite, optionB: AuditSamplingSite) => {
              if (
                sitesIdsToCheckAgainst.indexOf(optionA.siteId) >
                sitesIdsToCheckAgainst.indexOf(optionB.siteId)
              ) {
                return 1;
              } else {
                return -1;
              }
            },
          ),
        };
      },
    );
    this.validateSamplingData();
  }

  public showNotificationAndRedirectToProject() {
    Toast.open({
      message: `Changes saved`,
      position: 'is-top',
      type: 'is-dark',
      duration: 3500,
    });
  }

  public validateRangeAndUpdateChildSamplingSize(
    index: number,
    type: string,
    min: number,
    max: number,
  ) {
    let message: string = '';
    if (Number(max) > 0 && Number(max) < Number(min)) {
      if (type === 'min') {
        message = 'Min value cannot be greater than max value';
        this.auditCriteriaList[index].minSampleSize = null;
      } else {
        message = 'Max value cannot be less than min value';
        this.auditCriteriaList[index].maxSampleSize = null;
      }
      Toast.open({
        message,
        position: 'is-top',
        type: 'is-danger',
      });
    } else {
      this.updateChildSamplingSize(index, type);
    }
  }

  public handleMultiSiteChanges() {
    this.isButtonClick = true;
    this.validateSamplingData();
    if (!this.isButtonDisabled) {
      this.updateMultiSiteSampling({
        auditId: this.auditId,
        multiSitePayload: this.multiSitePayload,
        auditSamplingType: this.auditSamplingType,
        auditSamplingMode: this.consecutiveSamplingMode,
        validateDataEntry: this.validateDataEntry,
        samplingConfigId: this.auditConfig.auditSamplingConfigs.id,
      });
    }
  }

  public isParentSamplingValid(criteriaId: number, type: string): boolean {
    const criteriaDetails: any = this.invalidSamplingSites.find(
      (criteria) => criteria.criteriaId === criteriaId,
    );
    if (type === 'min') {
      return criteriaDetails && criteriaDetails.hasInvalidMin ? false : true;
    } else {
      return criteriaDetails && criteriaDetails.hasInvalidMax ? false : true;
    }
  }

  public isSiteSamplingValid(criteriaId: number, siteId: number): boolean {
    const criteriaDetails: any = this.invalidSamplingSites.find(
      (criteria) => criteria.criteriaId === criteriaId,
    );

    if (criteriaDetails) {
      const siteDetails: any = criteriaDetails.sites.find(
        (site: any) => site.siteId === siteId,
      );
      return siteDetails ? !siteDetails.hasInvalidRange : true;
    } else {
      return true;
    }
  }

  public validateRangeOnChange() {
    if (this.isButtonClick) {
      this.isButtonDisabled = false;
      this.tooltipMinErrorMessage = '';
      this.tooltipMaxErrorMessage = '';
      this.validateSamplingData();
    }
  }

  public validateSamplingData() {
    this.invalidSamplingSites = this.multiSitePayload.map((criteria) => {
      let totalMin: number = 0;
      let totalMax: number = 0;
      let invlidCriteriaMinValue = false;
      let invlidCriteriaMaxValue = false;
      let siteValueErrors = '';
      const sites: RangeSamplingValidation[] = criteria.sites.map((site) => {
        let inValidSiteRange = false;
        if (
          site.minSampleSize &&
          site.maxSampleSize &&
          Number(site.minSampleSize) > Number(site.maxSampleSize)
        ) {
          inValidSiteRange = true;
          this.isButtonDisabled = true;
          siteValueErrors = 'Min value cannot be greater than max value.';
        }
        return {
          siteId: site.siteId,
          hasInvalidRange: inValidSiteRange,
          minSampleSize: site.minSampleSize,
          maxSampleSize: site.maxSampleSize,
        };
      });
      if (siteValueErrors) {
        this.tooltipMinErrorMessage = siteValueErrors;
      }

      criteria.sites.forEach((site) => {
        totalMin += Number(site.minSampleSize);
        totalMax += Number(site.maxSampleSize);
      });
      if (
        totalMin &&
        criteria.minSampleSize &&
        totalMin < Number(criteria.minSampleSize)
      ) {
        invlidCriteriaMinValue = true;
        this.isButtonDisabled = true;
        this.tooltipMinErrorMessage = siteValueErrors
          ? `The total min value should be greater than equal to the sum of sites min value and ` +
            siteValueErrors
          : `The total min value should be greater than equal to the sum of sites min value.`;
      }
      if (
        totalMax &&
        criteria.maxSampleSize &&
        totalMax > Number(criteria.maxSampleSize)
      ) {
        invlidCriteriaMaxValue = true;
        this.isButtonDisabled = true;
        this.tooltipMaxErrorMessage = `The total max value should be less than equal to the sum of sites max value.`;
      }

      return {
        criteriaId: criteria.criteriaId,
        autoDistributeMinSampling: Number(criteria.minSampleSize)
          ? false
          : true,
        autoDistributeMaxSampling: Number(criteria.maxSampleSize)
          ? false
          : true,
        disableMinSiteInput: Number(criteria.minSampleSize) ? false : true,
        disableMaxSiteInput: Number(criteria.maxSampleSize) ? false : true,
        hasInvalidMin: invlidCriteriaMinValue,
        hasInvalidMax: invlidCriteriaMaxValue,
        minSampleSize: criteria.minSampleSize,
        maxSampleSize: criteria.maxSampleSize,
        sites,
      };
    });
  }

  public isSiteInputDisable(type: string, criteriaId: number) {
    const criteriaDetails: any = this.invalidSamplingSites.find(
      (criteria) => criteria.criteriaId === criteriaId,
    );

    return criteriaDetails
      ? type === 'min'
        ? criteriaDetails.disableMinSiteInput
        : criteriaDetails.disableMaxSiteInput
      : false;
  }

  public isAutoDistributeEnable(type: string, criteriaId: number) {
    const criteriaDetails: any = this.invalidSamplingSites.find(
      (criteria) => criteria.criteriaId === criteriaId,
    );

    return criteriaDetails
      ? type === 'min'
        ? criteriaDetails.autoDistributeMinSampling
        : criteriaDetails.autoDistributeMaxSampling
      : false;
  }

  public disableSiteInput(type: string, criteriaIndex: number) {
    this.invalidSamplingSites = this.invalidSamplingSites.map((criteria) => {
      if (
        criteria.criteriaId === this.auditCriteriaList[criteriaIndex].criteriaId
      ) {
        if (type === 'min') {
          criteria.disableMinSiteInput = false;
          criteria.autoDistributeMinSampling = false;
        } else {
          criteria.disableMaxSiteInput = false;
          criteria.autoDistributeMaxSampling = false;
        }
      }
      return criteria;
    });
  }

  public updateChildSamplingSize(index: number, type: string) {
    const min = Number(this.auditCriteriaList[index].minSampleSize);
    const max = Number(this.auditCriteriaList[index].maxSampleSize);
    const siteArrayLength = this.siteArray.length;
    if (type === 'min') {
      if (
        this.isAutoDistributeEnable(
          'min',
          this.auditCriteriaList[index].criteriaId,
        )
      ) {
        let modulo = min % siteArrayLength;
        const childVal = (min - modulo) / siteArrayLength;
        this.auditCriteriaList[index].sites.forEach(
          (currentVal: AuditSamplingSite, siteIndex: number) => {
            const tempModulo = modulo > 0 ? 1 : 0;
            this.auditCriteriaList[index].sites[siteIndex].minSampleSize =
              childVal + tempModulo;
            modulo -= 1;
          },
        );

        this.disableSiteInput('min', index);
      } else {
        this.validateRangeOnChange();
      }
    } else {
      if (
        this.isAutoDistributeEnable(
          'max',
          this.auditCriteriaList[index].criteriaId,
        )
      ) {
        let modulo = max % siteArrayLength;
        const childVal = (max - modulo) / siteArrayLength;
        this.auditCriteriaList[index].sites.forEach(
          (currentVal: AuditSamplingSite, siteIndex: number) => {
            const tempModulo = modulo > 0 ? 1 : 0;
            this.auditCriteriaList[index].sites[siteIndex].maxSampleSize =
              childVal + tempModulo;
            modulo -= 1;
          },
        );
        this.disableSiteInput('max', index);
      } else {
        this.validateRangeOnChange();
      }
    }
  }

  get auditCriteriaList() {
    return _orderBy(this.multiSitePayload, ['order'], ['asc']);
  }
}
</script>
<style lang="scss" module>
.tableName {
  color: #444f51;
  font-weight: bold;
}
.saveChangesBtn {
  float: right;
  border-radius: 24px;
  height: 48px;
  width: 120px;
  padding: 0 calc(1em + 0.25em);
}
.paddingTop {
  padding-top: 30px;
}
.criteriaBasedLabel {
  margin-top: 12px;
  padding-top: 24px;
}
.saveChangesContainer {
  padding-top: 60px;
  text-align: right;
}
.calculatorHeader {
  float: right;
}
.headerTitleColumn {
  padding: 0 0.75rem;
}
.numberInput {
  width: 80px !important;
  height: 44px;
  input {
    background-color: white !important;
    border-color: #dfdfdf !important;
    color: #444f51 !important;
    text-align: right;
    height: 44px;
    padding: 7px 7px;
  }

  input[disabled] {
    background-color: whitesmoke !important;
    border-color: whitesmoke !important;
    color: #7a7a7a !important;
  }
}
.invalidInput {
  input {
    border-color: #f14668 !important;
  }
}
.infoTooltip {
  display: inline-block !important;
}
.infoIcon {
  color: #f14668;
  margin-left: 8px !important;
}
input[type='number'] {
  -moz-appearance: textfield;
}
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
.criteriaBasedTable {
  thead {
    background-color: #ffffff;
    tr {
      font-size: 14px;
      th {
        color: #9f9f9f;
        font-weight: initial !important;
        padding: 20px 0px 23px 12px;
      }
      th:first-child {
        padding-left: 20px;
      }
      th:last-child {
        text-align: right;
        padding-left: 0;
        padding-right: 20px;
        div {
          float: right;
        }
      }
      th:nth-child(2) {
        padding-left: 40px;
      }
      th:nth-child(3) {
        float: right;
        text-align: right;
        padding-right: 20px;
        padding-left: 0px;
        div {
          float: right;
        }
      }
    }
  }
  tbody {
    background-color: #ffffff;
    tr {
      td {
        color: #444f51;
        border: 0.5px solid #dbdbdb;
        border-width: 0 0 0.5px;
        padding-top: 20px;
        padding-bottom: 18px;
        padding-right: 0px;
      }
      td:first-child {
        padding-left: 20px;
      }
      td:last-child {
        padding-left: 0;
        padding-right: 20px;
        padding-top: 20px;
      }
      td:nth-child(2) {
        padding-left: 40px;
      }
      td:nth-child(3) {
        width: 100%;
        float: right;
        padding-right: 20px;
        padding-left: 0px;
      }
    }
    tr:last-child {
      td {
        padding-bottom: 20px;
      }
    }
  }
}
.inputCheckbox {
  span {
    font-weight: unset !important;
  }
}
</style>
